/**
 * Canonical reader for SLEEP event records — every sleep consumer (sleep
 * page, dashboard, charts, services) imports from here so "is the baby asleep
 * right now" has exactly one definition. Mirrors feedShape.js / pumpingShape.js.
 *
 * Shapes (docs/ux-modernisation-spec.md §0.1, §3):
 *
 *   open sleep    { type:'SLEEP', startTime, endTime:null, data:{ category, notes } }
 *   closed sleep  { type:'SLEEP', startTime, endTime,      data:{ category, notes, durationMinutes } }
 *
 * Legacy rows that look open but are not (Alexa skill start/end items, the
 * temp-access quick log, quick-log entries that only carried a duration) are
 * why every predicate is defensive: a sleep is only "open" while it has no
 * end, no positive duration, a start time, and started within the last 16 h.
 * Anything older is stale — it shows in history as "still open" but never
 * drives a timer.
 *
 * Range helpers (`sleepOverlapMinutes`, `sleepOverlapsRange`) clip a sleep to
 * a [from, to] window so day totals can count an overnight sleep's
 * post-midnight portion without re-deriving "when did it end" per caller.
 */

/** A sleep that started more than this long ago can no longer be "open". */
export const OPEN_MAX_MS = 16 * 60 * 60 * 1000;

const toMs = (value) => {
  if (value === null || value === undefined || value === '') return NaN;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  return Date.parse(value);
};

const nowMs = (now) => {
  const ms = toMs(now);
  return Number.isNaN(ms) ? Date.now() : ms;
};

/**
 * True for any SLEEP row regardless of writer. Most rows carry
 * `entityType:'SLEEP'`; Alexa / temp-access rows carry `entityType:'EVENT'`
 * with `type:'SLEEP'`, so both fields are checked.
 */
export function isSleep(e) {
  if (!e) return false;
  return (e.entityType || e.type) === 'SLEEP' || e.type === 'SLEEP';
}

/**
 * open = SLEEP && !endTime && !(durationMinutes > 0) && startTime && now − startTime < 16 h
 */
export function isOpenSleep(e, now = Date.now()) {
  if (!isSleep(e)) return false;
  if (e.endTime) return false;
  if (Number(e.data?.durationMinutes) > 0) return false;
  if (!e.startTime) return false;
  const start = toMs(e.startTime);
  if (Number.isNaN(start)) return false;
  return nowMs(now) - start < OPEN_MAX_MS;
}

/**
 * When the sleep ended (Date), or `now` while it is still open, or null when
 * the row carries no usable end at all (stale open / malformed).
 */
export function sleepEnd(e, now = Date.now()) {
  if (!e) return null;
  if (e.endTime) {
    const end = toMs(e.endTime);
    return Number.isNaN(end) ? null : new Date(end);
  }
  const mins = Number(e.data?.durationMinutes);
  if (mins > 0) {
    const start = toMs(e.startTime || e.createdAt);
    return Number.isNaN(start) ? null : new Date(start + mins * 60000);
  }
  if (isOpenSleep(e, now)) return new Date(nowMs(now));
  return null;
}

/**
 * Minutes slept — elapsed-so-far for an open sleep, `endTime − startTime`
 * (or the stored duration) for a closed one, 0 when nothing usable.
 * Always derived; `durationMinutes` is never typed by the user.
 */
export function sleepDurationMinutes(e, now = Date.now()) {
  if (!e) return 0;
  const end = sleepEnd(e, now);
  const start = toMs(e.startTime || e.createdAt);
  if (!end || Number.isNaN(start)) return 0;
  return Math.max(0, Math.round((end.getTime() - start) / 60000));
}

/**
 * Milliseconds of the sleep [start, end-or-now] that fall inside
 * [fromMs, toMs]; 0 when there is no overlap or the sleep is unusable (no
 * start, stale open row, invalid range). Pure ms arithmetic, so a DST change
 * inside the range never shifts the answer.
 */
function sleepOverlapMs(e, rangeFrom, rangeTo, now) {
  if (!isSleep(e)) return 0;
  const start = toMs(e.startTime || e.createdAt);
  const end = sleepEnd(e, now);
  if (!end || Number.isNaN(start)) return 0;
  const from = toMs(rangeFrom);
  const to = toMs(rangeTo);
  if (Number.isNaN(from) || Number.isNaN(to)) return 0;
  const lo = Math.max(start, from);
  const hi = Math.min(end.getTime(), to);
  return hi > lo ? hi - lo : 0;
}

/**
 * Minutes of the sleep that fall inside [rangeFrom, rangeTo] — an open sleep
 * ends at `now`, a stale open row contributes 0. Use it for "sleep since
 * midnight" style totals so an overnight sleep counts only its post-midnight
 * portion and a sleep started today never counts minutes past midnight.
 */
export function sleepOverlapMinutes(e, rangeFrom, rangeTo, now = Date.now()) {
  return Math.round(sleepOverlapMs(e, rangeFrom, rangeTo, now) / 60000);
}

/** True when any part of the sleep [start, end-or-now] lies inside [rangeFrom, rangeTo]. */
export function sleepOverlapsRange(e, rangeFrom, rangeTo, now = Date.now()) {
  return sleepOverlapMs(e, rangeFrom, rangeTo, now) > 0;
}

/** Newest open sleep in `events`, or null. */
export function findOpenSleep(events, now = Date.now()) {
  if (!Array.isArray(events) || events.length === 0) return null;
  const at = nowMs(now);
  return (
    events
      .filter((e) => isOpenSleep(e, at))
      .sort((a, b) => toMs(b.startTime) - toMs(a.startTime))[0] || null
  );
}

/**
 * Pre-selects Nap vs Night for the start hero.
 *   ≥ 18:00 or < 05:00 local → 'night'
 *   within ±90 min of schedule.bedtime.start → 'night'
 *   otherwise → 'nap'
 */
export function suggestSleepCategory(now = new Date(), schedule = null) {
  const at = nowMs(now);
  const bedtime = toMs(schedule?.bedtime?.start);
  if (!Number.isNaN(bedtime) && Math.abs(at - bedtime) <= 90 * 60000) return 'night';
  const hour = new Date(at).getHours();
  if (hour >= 18 || hour < 5) return 'night';
  return 'nap';
}
